import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import Accordion from 'react-bootstrap/Accordion'

import HomeHeader from '../components/HomePageHeader'
import Footer from '../components/Footer'
import CTAButton from '../components/CTAButton/CTAButton'
import styles from './Creators.module.css'
import '../App.css'

const FAQS = [
  {
    q: 'Do I need a big following to qualify?',
    a: 'Nope. We care about the quality and authenticity of your video, not your follower count.'
  },
  {
    q: 'What kind of videos do well?',
    a: 'Demos, honest reviews, "I tried this tool" formats, tutorials — anything that genuinely shows off Tiiny Host. Raw and authentic beats polished and fake every time.'
  },
  {
    q: 'How many videos can I submit?',
    a: 'As many as you want. Each accepted video earns independently.'
  },
  {
    q: 'How do I get paid?',
    a: 'Reply back to the email you receive after submission when you hit 5k+ views and we can arrange payment.'
  },
  {
    q: 'What platforms are accepted?',
    a: 'TikTok, YouTube Shorts, Instagram Reels, X (Twitter) & LinkedIn'
  },
  {
    q: 'Can I use AI?',
    a: 'Yes you can but these videos do not generally perform well. We encourage genuine human created videos, see our examples above.'
  }
]

const WHY_ITEMS = [
  {
    icon: <img src="/assets/icons/currency-dollar.svg" alt="Money" style={{ width: '28px', height: '28px' }} />,
    title: 'Up to $50 per video',
    text: 'Paid per accepted submission — no waiting around.'
  },
  {
    icon: <img src="/assets/icons/globe.svg" alt="Platform" style={{ width: '24px', height: '24px' }} />,
    title: 'Multi-platform',
    text: 'TikTok, YouTube Shorts, Instagram Reels, X or LinkedIn.'
  },
  {
    icon: <img src="/assets/icons/cancel_black.svg" alt="No minimum" style={{ width: '18px', height: '18px' }} />,
    title: 'No follower minimum',
    text: 'A great video is a great video, period.'
  },
  {
    icon: <img src="/assets/icons/hand-pointing-upward.png" alt="No min" style={{ width: '20px', height: '20px' }} />,
    title: 'Submit as many as you like',
    text: "There's no cap on how many videos you can earn from."
  },
  {
    icon: <img src="/assets/icons/thunderbolt-black.svg" alt="Fast" style={{ width: '24px', height: '24px' }} />,
    title: 'Fast & simple product to cover',
    text: 'Tiiny Host practically sells itself — your audience will get it instantly.'
  },
  {
    icon: <img src="/assets/icons/sync.svg" alt="Flexibility" style={{ width: '24px', height: '24px' }} />,
    title: 'Flexibility',
    text: 'No lock-in, no minimums. Post when you want and earn.'
  }
]

const VideoCarousel = ({ videos }) => {
  // Start at middle index so videos appear on both sides
  const [currentIndex, setCurrentIndex] = useState(Math.floor(videos.length / 2))
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1))
  }

  // Extract YouTube video ID from URL
  const getYouTubeEmbedUrl = (url) => {
    let videoId = null

    // Handle various YouTube URL formats
    if (url.includes('youtube.com/shorts/')) {
      videoId = url.split('shorts/')[1]?.split('?')[0]
    } else if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0]
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0]
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : null
  }

  return (
    <div className={styles.carouselContainer}>
      <button
        className={`${styles.carouselArrow} ${styles.carouselArrowLeft}`}
        onClick={handlePrevious}
        aria-label="Previous video"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>

      <div className={styles.carouselWrapper}>
        <div className={styles.carouselTrack}>
          {videos.map((videoUrl, index) => {
            const offset = index - currentIndex
            const isFocused = offset === 0
            const absOffset = Math.abs(offset)

            // Calculate position: center the focused video
            // Adjust spacing based on screen size
            const videoWidth = isMobile ? (window.innerWidth <= 480 ? 200 : 250) : 300
            const gap = 20
            const translateX = offset * (videoWidth + gap)
            const embedUrl = getYouTubeEmbedUrl(videoUrl)

            return (
              <div
                key={index}
                className={`${styles.videoSlide} ${isFocused ? styles.videoSlideFocused : ''}`}
                style={{
                  opacity: absOffset > 2 ? 0 : isFocused ? 1 : 0.4,
                  transform: `translateX(${translateX}px) scale(${isFocused ? 1 : 0.85})`,
                  zIndex: isFocused ? 10 : 10 - absOffset,
                  pointerEvents: absOffset > 2 ? 'none' : 'auto'
                }}
              >
                {embedUrl ? (
                  <iframe
                    className={styles.video}
                    src={embedUrl}
                    title={`YouTube video ${index + 1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div
                    className={styles.video}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#000',
                      color: '#fff'
                    }}
                  >
                    Invalid video URL
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <button
        className={`${styles.carouselArrow} ${styles.carouselArrowRight}`}
        onClick={handleNext}
        aria-label="Next video"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>

      <div className={styles.carouselDots}>
        {videos.map((_, index) => (
          <button
            key={index}
            className={`${styles.carouselDot} ${index === currentIndex ? styles.carouselDotActive : ''}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to video ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

const Creators = () => {
  // Add your YouTube video URLs here (supports shorts, regular videos, and youtu.be links)
  const exampleVideos = [
    'https://www.youtube.com/shorts/VQeu3uGttAQ?feature=share',
    'https://youtube.com/shorts/1_gxzbLF-1c?feature=share',
    'https://youtube.com/shorts/c4pjUzi2Uoc?feature=share',
    'https://youtube.com/shorts/5WpCTskBfGg?feature=share',
    'https://youtube.com/shorts/UwKCoQvLQ6E?feature=share'
  ]

  return (
    <>
      <Helmet>
        <title>Tiiny Host Creator Program — Get Paid to Talk About Us</title>
        <meta
          name="description"
          content="Earn up to $50 per video. Record & post a clip about Tiiny Host on any platform and get paid. No experience needed. No minimum following required."
        />
        <meta property="og:title" content="Tiiny Host Creator Program — Get Paid to Talk About Us" />
        <meta
          property="og:description"
          content="Earn up to $50 per video. Post a clip about Tiiny Host on any platform and get paid."
        />
      </Helmet>

      <div className={`body-bg full-height ${styles.page}`}>
        <HomeHeader />

        {/* HERO */}
        <div className={styles.hero}>
          <div className={styles.eyebrow}>Introducing the Tiiny Host Creator Program</div>
          <h1 className={styles.heroTitle}>
            Get Paid to Talk About <span className="gradient-text">Tiiny</span>
          </h1>
          <p className={styles.heroSub}>
            Earn up to <strong>$50 per video</strong> — post a clip or short about Tiiny on any platform and we'll pay
            you for it.
          </p>

          <CTAButton
            className="m-auto"
            label="Submit video"
            variant="primary-gradient-round"
            size="large"
            onClick={() => window.open('https://tally.so/r/kdWRgZ', '_blank')}
            showArrow={true}
          />

          <p className={styles.socialProof}>No experience or minimum following required.</p>
        </div>

        {/* HOW IT WORKS */}
        <div className={styles.section} id="how">
          <h2 className={styles.sectionTitle}>Three Steps. Real Money.</h2>
          <div className={styles.stepsRow}>
            {[
              {
                n: 1,
                title: 'Record 🎥',
                text: 'Film a short clip featuring Tiiny.'
              },
              {
                n: 2,
                title: 'Post 🚀',
                text: 'Publish your video on your socials with #tiinyhost and tag @tiinyhost.'
              },
              {
                n: 3,
                title: 'Paid 🪙',
                text: "If your video crosses 10k views we'll pay you $50."
              }
            ].map(({ n, title, text }) => (
              <div key={n} className={styles.stepCard}>
                <div className={styles.stepNumber}>{n}</div>
                <div className={styles.stepTitle}>{title}</div>
                <div className={styles.stepText}>{text}</div>
              </div>
            ))}
          </div>
        </div>

        {/* TIMELINE */}
        <div className={styles.timelineSection}>
          <h2 className={styles.sectionTitle}>Rewards Path</h2>
          <div className={styles.timelineContainer}>
            <div className={styles.timelineTrack}>
              {/* Milestone 1: 0 views */}
              <div className={styles.milestone}>
                <div className={styles.milestoneMarker}>
                  <div className={styles.milestoneCircle}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                    </svg>
                  </div>
                  <div className={styles.milestoneLine}></div>
                </div>
                <div className={styles.milestoneContent}>
                  <div className={styles.milestoneValue}>0</div>
                  <div className={styles.milestoneLabel}>Views</div>
                  <div className={styles.milestoneDescription}>Video published</div>
                </div>
              </div>

              {/* Milestone 2: 5,000 views */}
              <div className={styles.milestone}>
                <div className={styles.milestoneMarker}>
                  <div className={`${styles.milestoneCircle} ${styles.milestoneCircleHighlight}`}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 12 20 22 4 22 4 12" />
                      <rect x="2" y="7" width="20" height="5" />
                      <line x1="12" y1="22" x2="12" y2="7" />
                      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                    </svg>
                  </div>
                  <div className={styles.milestoneLine}></div>
                </div>
                <div className={styles.milestoneContent}>
                  <div className={styles.milestoneValue}>5,000</div>
                  <div className={styles.milestoneLabel}>Views</div>
                  <div className={styles.milestoneReward}>🎁 Free Tiiny Host Account</div>
                </div>
              </div>

              {/* Milestone 3: 10,000 views */}
              <div className={styles.milestone}>
                <div className={styles.milestoneMarker}>
                  <div className={`${styles.milestoneCircle} ${styles.milestoneCircleGold}`}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="2" x2="12" y2="22" />
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  </div>
                  <div className={styles.milestoneLine}></div>
                </div>
                <div className={styles.milestoneContent}>
                  <div className={styles.milestoneValue}>10,000+</div>
                  <div className={styles.milestoneLabel}>Views</div>
                  <div className={styles.milestoneReward}>💰 $50 Payment</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Examples */}
        <div className={styles.whatSection} id="examples">
          <h2 className={styles.whatTitle}>What Should I Post?</h2>
          <h3 className="grey">Examples of shorts we like</h3>
          <VideoCarousel videos={exampleVideos} />
        </div>

        {/* WHY CREATORS LOVE THIS */}
        <div className={styles.section} style={{ paddingTop: 0 }}>
          <h2 className={styles.sectionTitle}>The Creator Program Built Around You</h2>
          <div className={styles.whyGrid}>
            {WHY_ITEMS.map(({ icon, title, text }) => (
              <div key={title} className={styles.whyCard}>
                <span className={styles.whyIcon}>{icon}</span>
                <div className="text-start">
                  <div className={styles.whyCardTitle}>{title}</div>
                  <div className={styles.whyCardText}>{text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className={styles.faqSection}>
          <h2 className={styles.faqTitle}>Frequently Asked Questions</h2>
          <Accordion flush>
            {FAQS.map(({ q, a }, i) => (
              <Accordion.Item key={i} eventKey={i}>
                <Accordion.Header className="font-18">{q}</Accordion.Header>
                <Accordion.Body className="text-start font-18">{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>

        {/* CLOSING CTA */}
        <div className={styles.closingSection}>
          <h2 className={styles.closingTitle}>
            Your Next Video <br />
            Could Be Worth $50.
          </h2>
          <CTAButton
            className="ms-auto me-auto mt-4"
            label="Apply Now"
            variant="primary-gradient-round"
            size="large"
            onClick={() => window.open('https://tally.so/r/kdWRgZ', '_blank')}
            showArrow={true}
          />
        </div>

        <Footer />
      </div>
    </>
  )
}

export default Creators
